import { useState } from "react";
import { Grid } from "@mui/material";
import MDBox from "components/MDBox";

import { useQuery } from "@tanstack/react-query";
import { useCustWhOptionsQuery } from "hooks/useQueryHook";

import { TitleButton } from "ui-components/Form";
import { Fieldset } from "ui-components/Form";
import { FormTitle, DatePickerField, Text, SelectField } from "ui-components/Form";

import { getDefShipZone } from "services/data.outbound.service";
import { getOrderType } from "services/data.outbound.service";
import {
  countryOptions,
  orderTypeOptions,
  shipmentTypeOptions,
} from "layouts/views/common/options";
import { getConsigneeOptions2 } from "services/data.internal.service";
import { getSOCustomerCarrier } from "services/data.outbound.service";
import { getCarrierList } from "services/data.internal.service";
import { getCountryList } from "services/data.internal.service";

function SO({ headerRender, customerCode,shipmentType, control, errors, setValue, getValues, mode = "ADD" }) {
  const [consigneeOptions, setConsigneeOptions] = useState([]);
  const [carriers, setCarries] = useState([]);
  const [countries, setCountries] = useState([]);

  useQuery({
    queryKey: ["consignee-options", { customerCode }],
    queryFn: () => getConsigneeOptions2({ customerCode }),
    enabled: Boolean(customerCode),
    onSuccess: (data) => {
      setConsigneeOptions(
        data.data.data.map((x) => {
          const { consigneeCode, ...consignee } = x;
          return {
            label: consigneeCode,
            value: consigneeCode,
            ...consignee,
          };
        })
      );
    },
  });

  useQuery({
    queryKey: ["carrier-options"],
    queryFn: () => getCarrierList({ active: true }),
    onSuccess: (data) => setCarries(data.data.data.map((x) => x.carrierCode))
  });

  useQuery({
    queryKey: ["country-options"],
    queryFn: () => getCountryList({ active: true }),
    onSuccess: (data) => setCountries(data.data.data.map((x) => x.country)),
  });

  const [postcode, setPostcode] = useState(null);

  const { refetch: refetchCarrier, isFetching } = useQuery({
    queryKey: ["carrier", { customerCode, postcode }],
    queryFn: () => getDefShipZone({ customerCode, postcode }),
    enabled: Boolean(customerCode) && Boolean(postcode),
    onSuccess: (data) => {
      if (data.data.data !== null) {
        const { shippingZone, carrierCode } = data.data.data;
        setValue("carrier", carrierCode);
        setValue("shippingZone", shippingZone);
      } else {
        setValue("carrier", null);
        setValue("shippingZone", null);
      }
    },
  });

  const onShipPostcodeChange = (e) => {
    const { value } = e.target;
    const shipCountry = getValues("shipCountry");
    if (shipCountry === "Malaysia") {
      setPostcode(value);
    }
    setValue("shipPostcode", value);
  };

  useQuery({
    queryKey: ["orderType", { customerCode }],
    queryFn: () => getOrderType({ customerCode }),
    enabled: Boolean(customerCode),
    onSuccess: (data) => {
      const orderType = data.data.data;
      setValue("orderType", orderType);
      if (orderType === "B2B") setValue("shipmentType", "NonIntegrated");
      else setValue("shipmentType", "Integrated");
    },
  });

  const { refetch } = useQuery({
    queryKey: ["customerCarrier", { customerCode }],
    queryFn: () => getSOCustomerCarrier({ customerCode }),
    enabled: false,
    onSuccess: (data) => {
      const customerCarrier = data.data.data;
      console.log(customerCarrier);
      if (customerCarrier !== null) {
        setValue("shippingZone", customerCarrier.shippingZone);
        setValue("carrier", customerCarrier.carrierCode);
      } else {
        setValue("shippingZone", null);
        setValue("carrier", null);
      }
    },
  });

  const onConsigneeChange = (e, option) => {
    if (option === null) {
      setValue("consigneeCode", option);
      return;
    }
    const {
      value,
      consigneeName,
      shipAddress1,
      shipAddress2,
      shipPostcode,
      shipCity,
      shipState,
      shipCountry,
      shipPhoneNo,
      billAddress1,
      billAddress2,
      billPostcode,
      billCity,
      billState,
      billCountry,
      billPhoneNo,
      contactEmail,
      contactPerson,
    } = option;
    setValue("consigneeCode", value);
    setValue("customerName", consigneeName);
    setValue("shipCompany", consigneeName);
    setValue("shipAddress1", shipAddress1);
    setValue("shipAddress2", shipAddress2);
    setValue("shipPostcode", shipPostcode);
    setValue("shipCity", shipCity);
    setValue("shipState", shipState);
    setValue("shipCountry", shipCountry);
    setValue("shipTelNo", shipPhoneNo);
    setValue("billCompany", consigneeName);
    setValue("billAddress1", billAddress1);
    setValue("billAddress2", billAddress2);
    setValue("billPostcode", billPostcode);
    setValue("billCity", billCity);
    setValue("billState", billState);
    setValue("billCountry", billCountry);
    setValue("billTelNo", billPhoneNo);
    setValue("shipContactPerson", contactPerson);
    setValue("billContactPerson", contactPerson);
    setValue("shipEmail", contactEmail);
    setValue("billEmail", contactEmail);
    if (shipCountry === "Malaysia") {
      setPostcode(shipPostcode);
    }
    if (shipCountry !== "Malaysia") {
      refetch();
    }
  };

  const onCountryChange = (e, option) => {
    if (option === "Malaysia") {
      setValue("shippingZone", null);
      setValue("carrier", null);
      if (Boolean(getValues("shipPostcode"))) {
        refetchCarrier();
      }
    } else {
      refetch();
    }
    setValue("shipCountry", option);
  };

  const applyBilling = () => {
    const {
      shipCompany,
      shipContactPerson,
      shipAddress1,
      shipAddress2,
      shipPostcode,
      shipCity,
      shipState,
      shipCountry,
      shipTelNo,
      shipEmail,
    } = getValues();
    setValue("billCompany", shipCompany);
    setValue("billContactPerson", shipContactPerson);
    setValue("billAddress1", shipAddress1);
    setValue("billAddress2", shipAddress2);
    setValue("billPostcode", shipPostcode);
    setValue("billCity", shipCity);
    setValue("billState", shipState);
    setValue("billCountry", shipCountry);
    setValue("billTelNo", shipTelNo);
    setValue("billEmail", shipEmail);
  };

  const onOrderTypeChange = (e, option) => {
    setValue("orderType", option);
    if (option === "B2B") setValue("shipmentType", "NonIntegrated");
    else setValue("shipmentType", "Integrated");
  };

  const [whOptions, setWhOptions] = useState([]);

  useCustWhOptionsQuery({
    customerCode,
    onSuccess: (data) => {
      const warehouses = data.data.data;
      if (warehouses.length > 0 && mode === "ADD") setValue("warehouseCode", warehouses[0]);
      setWhOptions(warehouses);
    },
  });

  function renderFn(property, index, length) {
    const { size, disabled, editable, ...data } = property;
    const xs = 6 / 2;
    const fieldProps = {
      ...data,
      control,
      errors,
      ...(disabled && { disabled }),
      ...(mode === "EDIT" && editable === false && { disabled: true, type: "text" }),
      ...(data.name === "shipPostcode" && { onBlur: onShipPostcodeChange }),
      ...(data.name === "customerName" && shipmentType === "SelfCollect" && { required:false }),
      ...(data.name === "shipAddress1" && shipmentType === "SelfCollect" && { required:false }),
      ...(data.name === "shipPostcode" && shipmentType === "SelfCollect" && { required:false }),
      ...(data.name === "shipCity" && shipmentType === "SelfCollect" && { required:false }),
      ...(data.name === "shipState" && shipmentType === "SelfCollect" && { required:false }),
      ...(data.name === "shipCountry" && shipmentType === "SelfCollect" && { required:false }),
      ...(data.name === "shipTelNo" && shipmentType === "SelfCollect" && { required:false }),
      ...(data.name === "shippingZone" && shipmentType === "SelfCollect" && { required:false }), 
    };
    let renderField;
    switch (fieldProps.type) {
      case "text":
      case "number":
        renderField = <Text field={fieldProps} />;
        break;
      case "select":
        let options = [];
        let onChange = null;
        switch (data.name) {
          case "consigneeCode":
            options = consigneeOptions;
            onChange = onConsigneeChange;
            break;
          case "carrier":
            options = carriers;
            break;
          case "orderType":
            options = orderTypeOptions;
            onChange = onOrderTypeChange;
            break;
          case "shipmentType":
            options = shipmentTypeOptions;
            break;
          case "warehouseCode":
            options = whOptions;
            break;
          case "shipCountry":
            options = countries;
            onChange = onCountryChange;
            break;
          case "billCountry":
            options = countries;
            break;
        }
        renderField = <SelectField field={fieldProps} options={options} onChange={onChange} />;
        break;
      case "date":
        renderField = <DatePickerField field={fieldProps} />;
        break;
      case "button":
        let button = null;
        button = <TitleButton label="Apply Shipping to Billing" onClick={applyBilling} />;
        renderField = button;
        break;
    }
    return (
      <Grid key={index} item xs={xs}>
        {renderField}
      </Grid>
    );
  }

  return (
    <MDBox>
      {isFetching && <span>fetching postcode..</span>}
      <FormTitle title={mode === "EDIT" ? "Edit Sales Order" : "Add Sales Order"} />
      <MDBox mt={1.625}>
        {headerRender.map((rows, i) => {
          if (rows[0].type === "fieldset") {
            return (
              <Fieldset label={rows[0].label} key={i}>
                {rows[0].fieldsetRender.map((fs_rows, fs_i) => (
                  <Grid container spacing={3} key={fs_i}>
                    {fs_rows.map((fs_row, fs_index) => renderFn(fs_row, fs_index, fs_rows.length))}
                  </Grid>
                ))}
              </Fieldset>
            );
          }
          return (
            <Grid container spacing={3} key={i}>
              {rows.map((row, index) => renderFn(row, index, rows.length))}
            </Grid>
          );
        })}
      </MDBox>
    </MDBox>
  );
}

export default SO;
